import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SimpleMDE from 'react-simplemde-editor';
import ReactMarkdown from 'react-markdown';
import { articlesAPI } from '../services/api';
import Header from '../components/Header';
import 'easymde/dist/easymde.min.css';
import './ArticleEditor.css';

const ArticleEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNewArticle = id === 'new';

  const [article, setArticle] = useState({
    article_id: '',
    article_title: '',
    article_content: '',
    tags: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (!isNewArticle) {
      loadArticle();
    }
  }, [id]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const response = await articlesAPI.getById(id);
      setArticle(response.data.article);
    } catch (error) {
      console.error('Failed to load article:', error);
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (!article.article_title.trim()) {
        setError('Article title is required');
        return;
      }

      if (!article.article_content.trim()) {
        setError('Article content is required');
        return;
      }

      if (isNewArticle) {
        // Generate article ID from title if not provided
        const articleId =
          article.article_id ||
          article.article_title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');

        const newArticle = {
          ...article,
          article_id: articleId,
        };

        await articlesAPI.create(newArticle);
        navigate(`/articles/${articleId}`);
      } else {
        await articlesAPI.update(id, {
          article_title: article.article_title,
          article_content: article.article_content,
          tags: article.tags,
        });
      }

      // Show success message
      alert(isNewArticle ? 'Article created!' : 'Article saved!');
    } catch (error) {
      console.error('Failed to save article:', error);
      setError(error.response?.data?.message || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !article.tags.includes(tagInput.trim())) {
      setArticle({
        ...article,
        tags: [...article.tags, tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setArticle({
      ...article,
      tags: article.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const editorOptions = {
    spellChecker: false,
    placeholder: 'Write your article content in Markdown...',
    toolbar: [
      'bold',
      'italic',
      'heading',
      '|',
      'quote',
      'unordered-list',
      'ordered-list',
      '|',
      'link',
      'image',
      '|',
      'preview',
      'side-by-side',
      'fullscreen',
      '|',
      'guide',
    ],
  };

  if (loading) {
    return (
      <div className="dashboard">
        <Header />
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Header />

      <div className="container editor-container">
        <div className="editor-header">
          <h2>{isNewArticle ? 'Create New Article' : 'Edit Article'}</h2>
          <div className="editor-actions">
            <button className="btn btn-secondary" onClick={() => navigate('/')}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Article'}
            </button>
          </div>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="editor-form">
          {/* Article ID (only for new articles) */}
          {isNewArticle && (
            <div className="form-group">
              <label htmlFor="article_id" className="form-label">
                Article ID (optional - auto-generated from title)
              </label>
              <input
                type="text"
                id="article_id"
                className="form-input"
                value={article.article_id}
                onChange={(e) =>
                  setArticle({ ...article, article_id: e.target.value })
                }
                placeholder="my-article-slug"
              />
            </div>
          )}

          {/* Article Title */}
          <div className="form-group">
            <label htmlFor="article_title" className="form-label">
              Article Title *
            </label>
            <input
              type="text"
              id="article_title"
              className="form-input"
              value={article.article_title}
              onChange={(e) =>
                setArticle({ ...article, article_title: e.target.value })
              }
              placeholder="Enter article title"
              required
            />
          </div>

          {/* Tags */}
          <div className="form-group">
            <label className="form-label">Tags</label>
            <div className="tags-input-container">
              <div className="tags-list">
                {article.tags.map((tag, idx) => (
                  <span key={idx} className="tag-item">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="tag-remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="tag-input-group">
                <input
                  type="text"
                  className="form-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Add a tag and press Enter"
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAddTag}
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Content Editor */}
          <div className="form-group">
            <div className="editor-toolbar-custom">
              <label className="form-label">Article Content *</label>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </button>
            </div>

            <div className={`editor-wrapper ${showPreview ? 'with-preview' : ''}`}>
              <div className="editor-pane">
                <SimpleMDE
                  value={article.article_content}
                  onChange={(value) =>
                    setArticle({ ...article, article_content: value })
                  }
                  options={editorOptions}
                />
              </div>

              {showPreview && (
                <div className="preview-pane">
                  <h3 className="preview-title">Preview</h3>
                  <div className="markdown-preview">
                    <ReactMarkdown>{article.article_content}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleEditor;
